/**
 * Geometry for face-tag overlays in the gallery lightbox. The lightbox image
 * renders with `object-fit: scale-down` inside a flexible box, so the drawn
 * image rectangle is usually smaller than the img element itself — letterboxed
 * on one axis, and never scaled above its natural size. Tag coordinates are
 * stored as normalized 0–1 floats relative to the image, so mapping a box onto
 * the screen means finding the drawn rectangle first.
 */

/** A width/height pair, in CSS pixels. */
export interface BoxSize {
  width: number;
  height: number;
}

/** A rectangle positioned within some containing element, in CSS pixels. */
export interface BoxRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** One tag's normalized 0–1 face box: top-left corner plus size. */
export interface NormalizedBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Where `object-fit: scale-down` actually draws the image inside its element:
 * scaled uniformly to fit (like `contain`) but never above natural size, and
 * centered on both axes. Returns null until real measurements exist — a zero
 * dimension means the image hasn't loaded or the element hasn't laid out.
 */
export function renderedImageRect(element: BoxSize, natural: BoxSize): BoxRect | null {
  if (element.width <= 0 || element.height <= 0 || natural.width <= 0 || natural.height <= 0) {
    return null;
  }
  const scale = Math.min(1, element.width / natural.width, element.height / natural.height);
  const width = natural.width * scale;
  const height = natural.height * scale;
  return {
    left: (element.width - width) / 2,
    top: (element.height - height) / 2,
    width,
    height,
  };
}

/** A normalized tag box in element pixels, given the drawn image rectangle. */
export function faceBoxRect(box: NormalizedBox, image: BoxRect): BoxRect {
  return {
    left: image.left + box.x * image.width,
    top: image.top + box.y * image.height,
    width: box.w * image.width,
    height: box.h * image.height,
  };
}
